import { ID } from '@ianesthesie-kit/services';
import {
  PatientId,
  useActiveSurgicalEventQuery,
  useUpdateSurgicalEventValidationFieldsMutation
} from './surgicalEvent.slice';
import { useDialogPromise } from '@ianesthesie-kit/redux';
import { SurgicalEventService } from '@/modules/surgicalEvent/surgicalEvent.service';
import { AppDialogType } from '@/config';
import { QueryResultLike } from '@/modules/surgicalEvent/util';
import { useMemo } from 'react';
import {
  MedicalDocument,
  MedicalDocumentType,
  useInsuranceDocumentsQuery
} from '@/modules/surgicalEvent/medicalDocument';
import {
  MedicalHo,
  MedicalHoItemCategory,
  useMedicalHoQuery
} from '@/modules/surgicalEvent/ho';
import { valueOfEnum } from '@ianesthesie-kit/core';
import { SurgicalEvent } from '@/modules/surgicalEvent/model';
import { useQueryForCurrentPatient } from '@/hooks';
import {
  SurgeryInfoService,
  useCurrentSurgeryInfo
} from '@/modules/surgicalEvent/surgeryInfo';
import { useParams } from 'next/navigation';

export const useCurrentPatientId = (): PatientId => {
  const { patientId } = useParams();

  return useMemo(() => {
    const id: ID = typeof patientId === 'string' ? patientId : '';
    return {
      patientId: id
    };
  }, [patientId]);
};

export const overwriteDataWithCurrent = <T, R extends QueryResultLike<T>>(
  res: R
): R => ({
  ...res,
  data: res.currentData
});

export const useDefaultQueryOptions = <T, R extends QueryResultLike<T>>() => {
  const { patientId } = useParams();

  return {
    skip: !patientId || typeof patientId !== 'string',
    selectFromResult: overwriteDataWithCurrent<T, R>
  };
};

export const useCurrentActiveSurgicalEvent = () =>
  useActiveSurgicalEventQuery(useCurrentPatientId(), useDefaultQueryOptions());

const selectPatient = <R extends QueryResultLike<SurgicalEvent>>(res: R) => ({
  ...res,
  data: res.data?.patient,
  currentData: res?.currentData?.patient
});

export const useCurrentPatient = () =>
  useActiveSurgicalEventQuery(useCurrentPatientId(), {
    ...useDefaultQueryOptions(),
    selectFromResult: selectPatient
  });

export const useValidateCurrentSurgicalEvent = () => {
  const patientId = useCurrentPatientId();
  const { waitForDialog } = useDialogPromise();
  const [upsert, ...mutationProps] =
    useUpdateSurgicalEventValidationFieldsMutation();

  async function handleValidate() {
    if (SurgicalEventService.isValidationWarningMuted) {
      return upsert(patientId);
    }

    return waitForDialog({
      dialogType: AppDialogType.MUTE_SE_WARNING,
      message: ''
    }).then((proceed) => {
      if (proceed) {
        return upsert(patientId);
      }
      return Promise.resolve(null);
    });
  }

  return [handleValidate, ...mutationProps] as const;
};

const selectIsPhoneVerified = <R extends QueryResultLike<SurgicalEvent>>(
  res: R
) => ({
  ...res,
  data: !!res?.data?.patient?.phoneNumberVerified,
  currentData: !!res?.data?.patient?.phoneNumberVerified
});

export const useCurrentIsPhoneNumberVerified = () => {
  const { patientId } = useCurrentPatientId();

  return useActiveSurgicalEventQuery(
    { patientId },
    {
      skip: !patientId,
      selectFromResult: selectIsPhoneVerified
    }
  );
};

const selectIsEmailVerified = <R extends QueryResultLike<SurgicalEvent>>(
  res: R
) => ({
  ...res,
  data: !!res?.data?.patient?.emailVerified,
  currentData: !!res?.data?.patient?.emailVerified
});

export const useCurrentIsEmailVerified = () => {
  const { patientId } = useCurrentPatientId();

  return useActiveSurgicalEventQuery(
    { patientId },
    {
      skip: !patientId,
      selectFromResult: selectIsEmailVerified
    }
  );
};

//   ALL BELOW SHOULD BE REFACTORED AS A RESULT OF https://app.clickup.com/t/86bxfb0cv  ///////////

export type PatientInsuranceInfo = {
  status?: {
    code: InsuranceStatus;
    translationCode: string;
  };
  support?: {
    code: 'YES' | 'NO' | 'IDK';
    translationCode: string;
  };
  policyDocument?: MedicalDocument;
  insuranceCard?: {
    front?: MedicalDocument;
    back?: MedicalDocument;
  };
};

export enum InsuranceStatus {
  INSURANCE = 'InsuranceStatus.Insurance',
  CMU = 'InsuranceStatus.Cmu',
  NO_INSURANCE = 'InsuranceStatus.NoInsurance'
}

export const useCurrentInsuranceInfo = (): {
  currentData: PatientInsuranceInfo | undefined;
  isFetching: boolean;
} => {
  const { patientId } = useCurrentPatientId();
  const { currentData: mho, isFetching: isFetchingMho } =
    useQueryForCurrentPatient<MedicalHo>({
      useQuery: useMedicalHoQuery
    });
  const { currentData: insuranceDocs, isFetching: isFetchingDocs } =
    useInsuranceDocumentsQuery(
      {
        patientId
      },
      {
        skip: !patientId
      }
    );

  const isFetching = isFetchingDocs || isFetchingMho;
  return useMemo(
    () => ({
      currentData: isFetching ? undefined : construct(mho, insuranceDocs),
      isFetching
    }),
    [insuranceDocs, mho, isFetching]
  );
};

function construct(
  mho: MedicalHo | undefined,
  insuranceDocs: MedicalDocument[] | undefined
): PatientInsuranceInfo {
  const info: PatientInsuranceInfo = {};

  if (insuranceDocs) {
    info.policyDocument = insuranceDocs?.find(
      (d) => d.type === MedicalDocumentType.INSURANCE_POLICY
    );
    info.insuranceCard = {
      back: insuranceDocs?.find(
        (d) => d.type === MedicalDocumentType.INSURANCE_CARD_BACK
      ),
      front: insuranceDocs?.find(
        (d) => d.type === MedicalDocumentType.INSURANCE_CARD_FRONT
      )
    };
  }

  const billingItems = mho?.[MedicalHoItemCategory.BILLING];

  const statusItem = billingItems?.find((it) => it.qcode === 'InsuranceStatus');
  if (statusItem) {
    info.status = {
      code: valueOfEnum(statusItem.code, InsuranceStatus, {
        throwIfNotFound: true
      })!,
      translationCode: statusItem.translationCode
    };
  }

  const supportItem = billingItems?.find(
    (it) => it.qcode === 'InsuranceSupport'
  );
  if (supportItem) {
    info.support = {
      code: supportItem.code as any,
      translationCode: supportItem.translationCode
    };
  }

  return info;
}

export const useCurrentIsPostop = () => {
  const { currentData: surgeryInfo } = useCurrentSurgeryInfo();

  return SurgeryInfoService.isPostop(surgeryInfo);
};

export const useCurrentIsPreop = () => {
  const { currentData: surgeryInfo } = useCurrentSurgeryInfo();

  return SurgeryInfoService.isPreop(surgeryInfo);
};
